/**
 * PolyRuntimeData: the reader for per-request browser globals.
 *
 * Every other component in this library receives its props through
 * `data-poly-content`, baked at publish time. `SearchResults` is the first
 * component that needs data the CMS cannot know: the results for THIS
 * request. Jellyfish's `JellyfishSearchController` injects those as
 * `window.searchResults` before the hydration bundle runs.
 *
 * This module is the single audited boundary for that untrusted per-request
 * payload. It owns the `typeof window` guard, the double-decode, the version
 * check, and the anchor-entry guard, so the fragile parsing is unit-tested
 * once instead of re-invented by every future consumer. When a second
 * consumer appears, promote this file to `src/utils/polyRuntimeData.ts`.
 *
 * The `window.searchResults` shape and its double-encoding are a v1 contract
 * owned by Jellyfish. The decode is pinned by unit tests so a drift fails CI
 * rather than production.
 */

/** Wire shape of one anchor deep-link. */
export interface SearchResultAnchorLink {
  /** Visible label for the sub-link. */
  displayName: string;
  /**
   * The fragment hash. The wire key is `link` (legacy `SearchAnchorLinksModel`
   * `{displayName, link}`), NOT `hash`. Jellyfish zips the authored
   * `_anchor.hash` content entry into this field. Rendered as
   * `href={`${url}#${link}`}`.
   */
  link: string;
}

/** Wire shape of one search result. */
export interface SearchResultItem {
  title: string;
  description?: string;
  url: string;
  /** Click-tracking `/redirect` URL. Falls back to `url` when absent. */
  'redirection-url'?: string;
  /**
   * Backend rank. The v1 contract delivers `results` already in rank order and
   * the component renders array order (the `<ol>` communicates it as ranking).
   * As defense in depth the reader stable-sorts by `rank` when EVERY surviving
   * entry carries a numeric one; with any rank absent the ordering intent is
   * ambiguous, so array order is trusted as delivered.
   */
  rank?: number;
  'anchor-links'?: SearchResultAnchorLink[];
}

/** Decoded, guarded runtime payload for the search page. */
export interface SearchRuntimeData {
  results: SearchResultItem[];
  original_query?: string;
  corrected_query?: string;
  /** Opaque, backend-tagged cursor. `null` means no further pages. */
  continuation?: string | null;
}

/** Raw outer wrapper Jellyfish assigns to `window.searchResults`. */
interface SearchResultsGlobal {
  /** Double-encoded JSON string holding `{ results, continuation }`. */
  search_result?: string;
  original_query?: string;
  corrected_query?: string;
  /**
   * Payload version. Present (`1`) on the adapter and PolyS arms, ABSENT on
   * the legacy arm. Absence therefore means "v1-compatible".
   */
  v?: number;
}

/** The only payload version this reader understands. */
const SUPPORTED_VERSION = 1;

/**
 * Reports an unexpected payload version to CloudWatch RUM.
 *
 * An unrecognized version is never a failure: the reader keeps parsing on the
 * v1 assumption so a Jellyfish-side version bump degrades to "renders with the
 * fields it knows" instead of an empty page. The metric is what tells us to
 * ship a v2 reader.
 */
function reportUnexpectedVersion(version: number, source: string): void {
  const cwr = (globalThis as { cwr?: (command: string, payload: unknown) => void }).cwr;
  if (typeof cwr !== 'function') {
    return;
  }
  try {
    cwr('recordEvent', {
      type: 'poly_search_runtime_data_version_unexpected',
      data: { version, expected: SUPPORTED_VERSION, source },
    });
  } catch {
    // Telemetry must never break rendering.
  }
}

/**
 * Checks the wrapper's `v` field. Absence is v1-compatible (legacy arm); an
 * unexpected value is metric'd and tolerated.
 */
function checkVersion(version: unknown, source: string): void {
  if (version === undefined || version === null) {
    return;
  }
  if (version !== SUPPORTED_VERSION) {
    reportUnexpectedVersion(Number(version), source);
  }
}

/**
 * Drops anchor entries missing either half of the pair.
 *
 * Jellyfish already length-guards the `_anchor.displayName` / `_anchor.hash`
 * zip, so a malformed entry should not reach us. This is defense in depth:
 * skip it, never throw, so one bad entry cannot cost the whole result row.
 *
 * `link` is stored without a leading `#`. Jellyfish forwards `_anchor.hash`
 * verbatim, which may already carry one, and the renderer adds its own; the
 * legacy LEGO page strips it the same way. Both fields are trimmed, because a
 * blank name leaves the link unnamed and a blank fragment points at the result
 * itself. The fragment is the render key, so a second entry for it is dropped.
 */
function coerceAnchorLinks(raw: unknown): SearchResultAnchorLink[] | undefined {
  if (!Array.isArray(raw)) {
    return undefined;
  }
  const anchors: SearchResultAnchorLink[] = [];
  const seen = new Set<string>();
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') {
      continue;
    }
    const { displayName, link } = entry as SearchResultAnchorLink;
    if (typeof displayName !== 'string' || typeof link !== 'string') {
      continue;
    }
    const name = displayName.trim();
    const fragment = link.trim().replace(/^#/, '');
    if (name.length > 0 && fragment.length > 0 && !seen.has(fragment)) {
      seen.add(fragment);
      anchors.push({ ...(entry as SearchResultAnchorLink), displayName: name, link: fragment });
    }
  }
  return anchors.length > 0 ? anchors : undefined;
}

/**
 * Derives a fallback title for a result with a non-empty `url` but no title.
 *
 * A url starting with `/` is returned unchanged (keeps any query or hash).
 * Otherwise, derives the pathname from the absolute URL via `new URL`. If the
 * URL does not parse or the pathname is empty, returns the raw `url` string.
 * The fallback is never empty, because the caller guarantees `url` is non-empty.
 */
function fallbackTitle(url: string): string {
  if (url.startsWith('/')) {
    return url;
  }
  try {
    const parsed = new URL(url);
    return parsed.pathname.length > 0 ? parsed.pathname : url;
  } catch {
    return url;
  }
}

/**
 * Normalizes a raw result list, dropping entries with no usable url and
 * applying fallback titles to entries with a url but no title.
 */
function coerceResults(raw: unknown): SearchResultItem[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  const results: SearchResultItem[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') {
      continue;
    }
    const item = entry as SearchResultItem;
    const url = typeof item.url === 'string' ? item.url.trim() : '';
    // An empty url renders an href that navigates back to the current page.
    if (url.length === 0) {
      continue;
    }
    // A result without a title still has a destination; the URL path gives its
    // link an accessible name.
    const trimmedTitle = typeof item.title === 'string' ? item.title.trim() : '';
    const title = trimmedTitle.length > 0 ? trimmedTitle : fallbackTitle(url);
    results.push({
      ...item,
      url,
      title,
      // `redirection-url` reaches `safeHref`, which calls `.trim()`, and
      // `description` is rendered directly. Dropping a non-string here keeps
      // the `url` fallback working instead of throwing at render time. A blank
      // tracking url is dropped too, or it would win over `url` and render an
      // empty href.
      'redirection-url': coerceString(item['redirection-url'])?.trim() || undefined,
      description: coerceString(item.description),
      'anchor-links': coerceAnchorLinks(item['anchor-links']),
    });
  }
  // See the `rank` field doc: sort only when the ordering intent is
  // unambiguous, meaning every surviving entry carries a numeric rank.
  // Array.prototype.sort is stable, so ties keep their delivered order.
  if (results.length > 1 && results.every(result => typeof result.rank === 'number')) {
    results.sort((a, b) => (a.rank as number) - (b.rank as number));
  }
  return results;
}

/**
 * Returns the value only when it really is a string.
 *
 * `results` and anchor entries are guarded above, but the query and cursor
 * fields reach string operations too: the queries become the regex source for
 * title bolding, and the cursor goes through `encodeURIComponent`. A non-string
 * from the injector would throw at render time, which is the exact failure this
 * module exists to prevent.
 *
 * Callers collapse the cursor with `||` rather than `??`, because an
 * empty-string cursor must read as exhaustion. Left as `""` it would keep the
 * load-more control visible while every click short-circuited on a falsy token.
 */
function coerceString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

/**
 * Reads the per-request search payload from `window.searchResults`.
 *
 * SSR-safe. Returns `null` on the server, when the global is absent, when
 * `search_result` is missing or empty, and when the encoded payload is
 * malformed. That `null` path is the fix for the legacy bare-`/search`
 * TypeError: the old page called `JSON.parse` on `undefined` and threw, which
 * is why a query-less `/search` logged a console error. Here it renders the
 * prompt state instead.
 */
export function readPolyRuntimeData(): SearchRuntimeData | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const raw = (window as unknown as { searchResults?: SearchResultsGlobal }).searchResults;
  if (!raw || !raw.search_result) {
    // Absent global, or the query-less request Jellyfish serves with no
    // payload. Both mean "nothing to render", not "something broke".
    return null;
  }

  checkVersion(raw.v, 'window.searchResults');

  let decoded: unknown;
  try {
    // Second decode: the outer object is already JS, `search_result` is still
    // a JSON string inside it.
    decoded = JSON.parse(raw.search_result);
  } catch (error) {
    // Absent payloads are normal (bare /search); a payload that EXISTS but
    // does not parse means the injector shipped something broken, which is
    // worth a diagnostic even though the page degrades to the prompt state.
    // eslint-disable-next-line no-console
    console.warn('[SearchResults] window.searchResults.search_result is not valid JSON:', error);
    return null;
  }

  if (!decoded || typeof decoded !== 'object') {
    return null;
  }

  const payload = decoded as { results?: unknown; continuation?: unknown };
  return {
    results: coerceResults(payload.results),
    original_query: coerceString(raw.original_query),
    corrected_query: coerceString(raw.corrected_query),
    continuation: coerceString(payload.continuation) || null,
  };
}

/** One page of results fetched from the continuation endpoint. */
export interface SearchContinuationPage {
  results: SearchResultItem[];
  continuation: string | null;
}

/**
 * Parses a `GET /search/results` response body.
 *
 * Single-encoded (the body IS the payload), unlike the injected global.
 * Returns `null` on a shape the component cannot use, which the caller
 * surfaces as the retryable error state.
 *
 * A `corrected_query` echoed here is deliberately ignored: the correction is a
 * property of the original query and is already displayed from page 1, and the
 * continuation request already carried the corrected query, so adopting it
 * mid-stream would only risk changing the effective query between pages.
 */
export function parseContinuationPage(payload: unknown): SearchContinuationPage | null {
  if (!payload || typeof payload !== 'object') {
    return null;
  }
  const body = payload as { results?: unknown; continuation?: unknown; v?: number };
  if (!Array.isArray(body.results)) {
    return null;
  }
  checkVersion(body.v, 'continuation-response');
  return {
    results: coerceResults(body.results),
    continuation: coerceString(body.continuation) || null,
  };
}
